import { Heading, Section, Stack } from "@carbon/react";
import { FC, useState } from "react";
import { FormProvider } from "react-hook-form";
import { v4 } from "uuid";

import { Button } from "src/components/Buttons";
import { ModalTransition } from "src/components/Modal";
import { IdentifierFilterableSinglePickerFormField } from "src/components/Pickers/IdentifierFilterableSinglePickerFormField";
import { useT } from "src/internationalization";

import {
  SecondaryCriteria,
  SecondaryCriteriaModal,
  SecondaryCriteriaTable,
} from "../SecondaryCriteria";

import { IdentifierFormControl } from "./useIdentifierForm";

export type IdentifierFormProps = {
  identifierFormControl: IdentifierFormControl;
  caseTypeCode?: string;
};

export const IdentifierForm: FC<IdentifierFormProps> = ({
  identifierFormControl,
  caseTypeCode,
}) => {
  const t = useT();
  const [showSecondaryCriteria, setShowSecondaryCriteria] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<SecondaryCriteria | null>(null);

  const handleAddSecondaryCriteria = () => {
    setShowSecondaryCriteria(true);
  };

  const handleSaveSecondaryCriteria = (
    data: Omit<SecondaryCriteria, "id">,
    e?: React.FormEvent
  ) => {
    if (e) {
      e.preventDefault();
    }

    if (editingCriteria) {
      identifierFormControl.setSecondaryCriteriaList((prev) =>
        prev.map((item) => (item.id === editingCriteria.id ? { ...item, ...data } : item))
      );
    } else {
      identifierFormControl.setSecondaryCriteriaList((prev) => [
        ...prev,
        {
          id: v4(),
          ...data,
        },
      ]);
    }
    handleCancelSecondaryCriteria();
  };

  const handleCancelSecondaryCriteria = () => {
    setShowSecondaryCriteria(false);
    setEditingCriteria(null);
  };

  const handleEditCriteria = (criteria: SecondaryCriteria) => {
    setEditingCriteria(criteria);
    setShowSecondaryCriteria(true);
  };

  const handleDeleteCriteria = (id: string) => {
    if (editingCriteria?.id === id) {
      setShowSecondaryCriteria(false);
      setEditingCriteria(null);
    }

    identifierFormControl.setSecondaryCriteriaList((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <>
      <FormProvider {...identifierFormControl.methods}>
        <Stack gap={6} className="mb-4">
          <div className="flex flex-col gap-6">
            <Section level={3}>
              <Heading className={"ml-0"}>
                {t("page.rules.modal.create.linkedCase.form.condition.title")}
              </Heading>
            </Section>
            <IdentifierFilterableSinglePickerFormField
              id="identifier"
              name="identifier"
              label={t("page.rules.modal.create.form.type.element.title")}
              helperText={t("page.rules.modal.create.form.type.element.helper")}
              rules={{ required: t("general.form.field.required") }}
              caseTypeCode={caseTypeCode}
            />
          </div>
          <div className={"flex flex-col gap-6"}>
            <Section level={3}>
              <Heading className={"ml-0"}>
                {t("page.rules.modal.create.linkedCase.secondary.criteria.title")}
              </Heading>
            </Section>
            {identifierFormControl.secondaryCriteriaList.length > 0 && (
              <SecondaryCriteriaTable
                data={identifierFormControl.secondaryCriteriaList}
                onEdit={handleEditCriteria}
                onDelete={handleDeleteCriteria}
              />
            )}
            <Button onClick={handleAddSecondaryCriteria} kind="tertiary">
              {t("page.rules.modal.create.linkedCase.secondary.criteria.button")}
            </Button>
            {!showSecondaryCriteria && identifierFormControl.secondaryCriteriaList.length === 0 && (
              <div>
                <span className="font-bold text-textSecondary">
                  {t("page.rules.modal.create.linkedCase.form.secondary.criteria.title")}
                </span>
                <div>
                  <p className="text-xs text-textSecondary">
                    {t(
                      "page.rules.modal.create.linkedCase.form.secondary.criteria.description.part1"
                    )}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {t(
                      "page.rules.modal.create.linkedCase.form.secondary.criteria.description.part2"
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Stack>
      </FormProvider>
      <ModalTransition>
        {showSecondaryCriteria && (
          <SecondaryCriteriaModal
            onSave={handleSaveSecondaryCriteria}
            onCancel={handleCancelSecondaryCriteria}
            isEditing={!!editingCriteria}
            initialData={editingCriteria || undefined}
            caseTypeCode={caseTypeCode}
          />
        )}
      </ModalTransition>
    </>
  );
};
